import React, { useState, useEffect, useMemo, useRef } from 'react';
import LinkButton from './LinkButton';
import { diffLines, diffChars } from 'diff';

const QuestDiffViewer = ({ questId, onClose, backHref, canPushToProd = true }) => {
  const [testData, setTestData] = useState(null);
  const [prodData, setProdData] = useState(null);
  const [audioDiff, setAudioDiff] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const diffContainerRef = useRef(null);
  const [currentChangeIndex, setCurrentChangeIndex] = useState(-1);
  const [isSyncing, setIsSyncing] = useState(false);
  const [showSyncConfirm, setShowSyncConfirm] = useState(false);
  const [diffMode, setDiffMode] = useState('lines'); // 'lines' or 'chars'
  const [renderTrigger, setRenderTrigger] = useState(0);
  const changeElementsRef = useRef([]);

  useEffect(() => {
    const fetchQuestData = async () => {
      if (!questId) {
        setError('No Quest ID provided for comparison.');
        setLoading(false);
        return;
      }

      const testQuestId = questId;
      const prodQuestId = questId.endsWith('_test') ? questId.slice(0, -5) : questId;

      setLoading(true);
      setError(null);
      try {
        // Fetch test data, prod data, and the voice-over diff in parallel.
        // Audio lives outside quest_data, so it needs its own comparison.
        const [testRes, prodRes, audioRes] = await Promise.all([
          fetch(`/api/quest/${testQuestId}?table=quests_test`),
          fetch(`/api/quest/${prodQuestId}?table=quests`),
          fetch(`/api/quest-audio-diff/${testQuestId}`)
        ]);

        if (audioRes.ok) {
          setAudioDiff(await audioRes.json());
        } else {
          setAudioDiff([]);
        }

        // Check for fetch errors
        if (!testRes.ok) {
          throw new Error(`Failed to fetch test data: ${testRes.status} ${testRes.statusText}`);
        }
        
        const testJson = await testRes.json();
        let prodJson;

        if (prodRes.ok) {
          prodJson = await prodRes.json();
        } else if (prodRes.status === 404) {
          prodJson = { message: "This quest does not exist in production yet." };
        } else {
          throw new Error(`Failed to fetch production data: ${prodRes.status} ${prodRes.statusText}`);
        }

        setTestData(testJson);
        setProdData(prodJson);
      } catch (err) {
        console.error('Error fetching quest data for diff:', err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchQuestData();
  }, [questId]);

  // Stable stringify and strings for both modes
  const { prodString, testString } = useMemo(() => {
    if (!testData || !prodData) return { prodString: '', testString: '' };
    const stableJsonStringify = (obj) => {
      const allKeys = new Set();
      JSON.stringify(obj, (key, value) => {
        if (typeof value === 'object' && value !== null && !Array.isArray(value)) {
          Object.keys(value).forEach(k => allKeys.add(k));
        }
        return value;
      });
      const sortedKeys = Array.from(allKeys).sort();
      return JSON.stringify(obj, sortedKeys, 2);
    };
    return {
      prodString: stableJsonStringify(prodData),
      testString: stableJsonStringify(testData)
    };
  }, [testData, prodData]);

  // Always compute line-based diffs for navigation and stats
  const lineDiffs = useMemo(() => {
    if (!prodString && !testString) return [];
    return diffLines(prodString, testString);
  }, [prodString, testString]);

  useEffect(() => {
    // This effect forces a re-render whenever the diffs or mode change.
    // This helps ensure that the ref callbacks in the render function have been executed
    // before the user can click the navigation buttons, which is a known issue in some
    // React production build scenarios.
    setRenderTrigger(prev => prev + 1);
  }, [lineDiffs, diffMode]);

  // Count added/removed lines from line-based diffs
  const lineCounts = useMemo(() => {
    if (!lineDiffs) return { added: 0, removed: 0 };
    let added = 0;
    let removed = 0;
    lineDiffs.forEach(part => {
      const lines = part.value.split('\n').filter((line, i, arr) => i < arr.length - 1 || line !== '').length;
      if (part.added) {
        added += lines;
      } else if (part.removed) {
        removed += lines;
      }
    });
    return { added, removed };
  }, [lineDiffs]);

  // Identify change block start indices within lineDiffs (consecutive added/removed grouped)
  const changeBlockStartIndices = useMemo(() => {
    const blocks = [];
    lineDiffs.forEach((part, index) => {
      if (part.added || part.removed) {
        if (index === 0 || (!lineDiffs[index - 1].added && !lineDiffs[index - 1].removed)) {
          blocks.push(index);
        }
      }
    });
    return blocks;
  }, [lineDiffs]);

  const totalChanges = changeBlockStartIndices.length;

  // Reset navigation and refs when diffs change
  useEffect(() => {
    setCurrentChangeIndex(-1);
  }, [lineDiffs]);

  // Find the nearest scrollable ancestor for a given element
  const getScrollableAncestor = (element) => {
    let node = element?.parentElement;
    while (node) {
      const hasScrollableSpace = node.scrollHeight > node.clientHeight;
      const overflowYStyle = window.getComputedStyle(node).overflowY;
      const isScrollable = hasScrollableSpace && (overflowYStyle === 'auto' || overflowYStyle === 'scroll');
      if (isScrollable) return node;
      node = node.parentElement;
    }
    return null;
  };

  // Animate vertical scroll of a container to a target position with a fixed duration
  const animateVerticalScroll = (container, to, durationMs = 250) => {
    const start = container.scrollTop;
    const change = to - start;
    const startTime = performance.now();

    const easeInOutQuad = (t) => (t < 0.5 ? 2 * t * t : -1 + (4 - 2 * t) * t);

    const step = (now) => {
      const elapsed = now - startTime;
      const progress = Math.min(1, elapsed / durationMs);
      const eased = easeInOutQuad(progress);
      container.scrollTop = start + change * eased;
      if (progress < 1) requestAnimationFrame(step);
    };

    requestAnimationFrame(step);
  };

  const handleGoToChange = (direction) => {
    if (totalChanges === 0) return;

    let nextIndex;
    if (currentChangeIndex === -1) {
      nextIndex = direction === 'next' ? 0 : totalChanges - 1;
    } else {
      nextIndex = direction === 'next'
        ? (currentChangeIndex + 1) % totalChanges
        : (currentChangeIndex - 1 + totalChanges) % totalChanges;
    }

    const targetElement = changeElementsRef.current[nextIndex];
    if (targetElement) {
      const scrollContainer = getScrollableAncestor(targetElement) || document.scrollingElement || document.documentElement;

      // Compute desired scrollTop to center the target element within the container
      const containerRect = scrollContainer.getBoundingClientRect();
      const elementRect = targetElement.getBoundingClientRect();
      const currentTop = scrollContainer.scrollTop;
      const offsetWithinContainer = elementRect.top - containerRect.top;
      const centerOffset = (scrollContainer.clientHeight / 2) - (Math.min(targetElement.clientHeight, scrollContainer.clientHeight) / 2);
      const targetScrollTop = currentTop + offsetWithinContainer - centerOffset;

      animateVerticalScroll(scrollContainer, targetScrollTop, 250);
      setCurrentChangeIndex(nextIndex);
    }
  };

  const confirmSyncToProd = async () => {
    if ((totalChanges === 0 && !hasAudioChanges) || !testData) return;

    const prodQuestId = questId.endsWith('_test') ? questId.slice(0, -5) : questId;

    setIsSyncing(true);
    setError(null);
    try {
      const response = await fetch(`/api/sync-quest/${prodQuestId}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ questData: testData }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to sync to production.');
      }

      alert('Successfully synced to production!');
      onClose();
    } catch (err) {
      console.error('Sync error:', err);
      setError(err.message);
    } finally {
      setIsSyncing(false);
      setShowSyncConfirm(false);
    }
  };

  const handleSyncToProd = () => {
    setShowSyncConfirm(true);
  };

  const hasChanges = useMemo(() => lineDiffs.some(part => part.added || part.removed), [lineDiffs]);
  const hasAudioChanges = audioDiff.length > 0;

  // Render helper: render a block of consecutive change parts either line-level or char-level
  const renderChangeBlock = (startIndex, blockIndex) => {
    // Determine the span of the block
    let endIndex = startIndex;
    while (endIndex + 1 < lineDiffs.length && (lineDiffs[endIndex + 1].added || lineDiffs[endIndex + 1].removed)) {
      endIndex++;
    }

    const parts = lineDiffs.slice(startIndex, endIndex + 1);

    if (diffMode === 'lines') {
      return (
        <div key={`block-${startIndex}`} ref={el => { changeElementsRef.current[blockIndex] = el; }}>
          {parts.map((part, idx) => {
            const className = part.added ? 'added' : part.removed ? 'removed' : '';
            const prefix = part.added ? '+' : part.removed ? '-' : ' ';
            const lines = part.value.split('\n').filter((line, i, arr) => i < arr.length - 1 || line !== '');
            return lines.map((line, li) => (
              <div key={`p${idx}-l${li}`} className={`diff-line ${className}`}>
                <span className="diff-line-prefix">{prefix}</span>
                <span className="diff-line-content">{line}</span>
              </div>
            ));
          })}
        </div>
      );
    }

    // chars mode: compute char diffs between concatenated old/new within the block
    const oldText = parts.filter(p => p.removed).map(p => p.value).join('');
    const newText = parts.filter(p => p.added).map(p => p.value).join('');
    const charDiffs = diffChars(oldText, newText);

    return (
      <div key={`block-${startIndex}`} ref={el => { changeElementsRef.current[blockIndex] = el; }}>
        {charDiffs.map((chunk, i) => {
          if (chunk.added) {
            return <span key={i} className="diff-char added">{chunk.value}</span>;
          }
          if (chunk.removed) {
            return <span key={i} className="diff-char removed">{chunk.value}</span>;
          }
          return <span key={i}>{chunk.value}</span>;
        })}
      </div>
    );
  };

  const renderDiff = () => {
    // Clear the refs array at the beginning of each render.
    changeElementsRef.current = [];
    const elements = [];
    let blockCounter = 0;

    for (let i = 0; i < lineDiffs.length; i++) {
      const part = lineDiffs[i];
      if (part.added || part.removed) {
        // Start of a change block
        if (i === 0 || (!lineDiffs[i - 1].added && !lineDiffs[i - 1].removed)) {
          elements.push(renderChangeBlock(i, blockCounter));
          // Skip to end of the block
          while (i + 1 < lineDiffs.length && (lineDiffs[i + 1].added || lineDiffs[i + 1].removed)) {
            i++;
          }
          blockCounter++;
          continue;
        }
      }
      // Unchanged part: render lines plainly
      const lines = part.value.split('\n').filter((line, idx, arr) => idx < arr.length - 1 || line !== '');
      elements.push(
        <div key={`plain-${i}`}>
          {lines.map((line, li) => (
            <div key={`plain-${i}-${li}`} className={`diff-line`}>
              <span className="diff-line-prefix"> </span>
              <span className="diff-line-content">{line}</span>
            </div>
          ))}
        </div>
      );
    }

    return elements;
  };

  return (
    <>
      <div className="diff-viewer-container">
        <div className="diff-viewer-header">
          <div className="diff-header-info">
            <h1>Comparing Quest: {questId}</h1>
            {hasChanges && (
              <div className="diff-stats">
                <span className="stats-added">+{lineCounts.added} added</span>
                <span className="stats-removed">-{lineCounts.removed} removed</span>
              </div>
            )}
            <div className="diff-legend">
            <div className="legend-item"><div className="legend-color-box legend-removed"></div> In Production (Old)</div>
              <div className="legend-item"><div className="legend-color-box legend-added"></div> In Test (New)</div>
            </div>
          </div>
          <div className="diff-actions">
            {totalChanges > 0 && (
              <div className="diff-navigation">
                <button onClick={() => handleGoToChange('prev')} title="Go to previous change" disabled={totalChanges === 0}>↑ Prev</button>
                <span>{currentChangeIndex === -1 ? 0 : currentChangeIndex + 1} of {totalChanges}</span>
                <button onClick={() => handleGoToChange('next')} title="Go to next change" disabled={totalChanges === 0}>Next ↓</button>
              </div>
            )}
            <div className="diff-mode-toggle">
              <button onClick={() => setDiffMode('lines')} className={diffMode === 'lines' ? 'active' : ''}>Lines</button>
              <button onClick={() => setDiffMode('chars')} className={diffMode === 'chars' ? 'active' : ''}>Chars</button>
            </div>
            <LinkButton href={backHref} onClick={onClose} className="back-button">Back to Editor</LinkButton>
            {canPushToProd && (
              <button
                className="sync-button"
                disabled={(!hasChanges && !hasAudioChanges) || isSyncing}
                onClick={handleSyncToProd}
              >
                {isSyncing ? 'Syncing...' : 'Sync to Production'}
              </button>
            )}
          </div>
        </div>

        {loading && <div className="loading">Loading comparison...</div>}
        {error && !isSyncing && <div className="error">{error}</div>}

        {!loading && (
          <>
            {hasAudioChanges && (
              <div className="audio-diff-section" style={{ padding: '10px 14px', borderBottom: '1px solid #333' }}>
                <h3 style={{ margin: '0 0 8px' }}>🎵 Voice-over changes ({audioDiff.length})</h3>
                <ul style={{ listStyle: 'none', margin: 0, padding: 0 }}>
                  {audioDiff.map((a, i) => {
                    const color = a.status === 'added' ? '#3fb950' : a.status === 'removed' ? '#f85149' : '#d29922';
                    const label = a.status === 'added' ? 'added' : a.status === 'removed' ? 'removed' : 'changed';
                    return (
                      <li key={i} style={{ padding: '2px 0' }}>
                        <span style={{ color, fontWeight: 600, textTransform: 'uppercase', fontSize: '0.8em', marginRight: 8 }}>{label}</span>
                        <span>{a.location_name || a.location_id || a.location_uuid}</span>
                        <span style={{ opacity: 0.7 }}> · {a.field_type}</span>
                      </li>
                    );
                  })}
                </ul>
              </div>
            )}
            <div className="diff-content" ref={diffContainerRef}>
              {lineDiffs.length > 0
                ? renderDiff()
                : <p>{hasAudioChanges ? 'No text differences (voice-over changes above).' : 'No differences found.'}</p>}
            </div>
          </>
        )}
      </div>

      {showSyncConfirm && (
        <div className="sync-confirm-overlay">
          <div className="sync-confirm-dialog">
            <h2>Confirm Sync</h2>
            <p>
              Are you sure you want to overwrite the production data for quest 
              <code>{questId.endsWith('_test') ? questId.slice(0, -5) : questId}</code> 
              with the current test version?
            </p>
            {hasAudioChanges && (
              <p>This will also promote {audioDiff.length} voice-over change{audioDiff.length === 1 ? '' : 's'} to production.</p>
            )}
            <p><strong>This action cannot be undone.</strong></p>
            <div className="sync-confirm-buttons">
              <button onClick={() => setShowSyncConfirm(false)} className="cancel-sync-button">Cancel</button>
              <button onClick={confirmSyncToProd} className="confirm-sync-button">Yes, Sync to Production</button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default QuestDiffViewer;
